<script setup lang="ts">
import { Mailing } from '@/@types'
import { Item } from '@/@types/cart'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'

import { useForm } from '@inertiajs/vue3'
import { Loader2, Trash2, User } from 'lucide-vue-next'
import { ref } from 'vue'

defineProps<{
    item: Item
    mailing: Mailing
    isAdmin: boolean
}>()

const form = useForm({})

const emit = defineEmits<{
    (e: 'delete', mailingId: number, itemId?: number): void
}>()

const showDialog = ref(false)
</script>

<template>
    <Dialog v-model:open="showDialog">
        <DialogOverlay class="z-60" />
        <DialogTrigger :disabled="!isAdmin && item.ticket.workshop.disable_actions" class="hover:not(:disabled):text-destructive disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-gray-400">
            <Trash2 class="w-6" />
        </DialogTrigger>
        <DialogContent class="lg:max-w-lg">
            <DialogHeader>
                <DialogTitle>{{ $t('Are you revoking an invitation?') }}</DialogTitle>
                <DialogDescription class="text-foreground">
                    {{ $t('Upon confirmation, an email will be sent to the participant informing them that the ticket is no longer available.') }}
                </DialogDescription>
            </DialogHeader>

            <div class="flex gap-3 bg-gray-50 p-4">
                <div class="flex h-11 min-w-11 items-center justify-center rounded-full bg-cyan-50 text-cyan-700">
                    <User class="size-5" />
                </div>

                <div>
                    <h4 class="font-semibold text-slate-800">
                        {{ mailing.personal_info.full_name }}
                    </h4>
                    <p class="text-sm text-slate-800">
                        {{ $t(item.ticket.category) }}
                    </p>
                </div>
            </div>

            <DialogFooter>
                <form @submit.prevent="mailing.id && emit('delete', mailing.id, item.id); showDialog = false" class="w-full">
                    <div class="flex flex-1 items-center justify-center gap-2">
                        <Button
                            @click="showDialog = false"
                            variant="ghost"
                            type="button"
                            class="text-primary hover:bg-primary/10 hover:text-primary w-full flex-1"
                        >
                            {{ $t('Back') }}
                        </Button>
                        <Button :disabled="form.processing" class="flex-1">
                            <template v-if="!form.processing">
                                {{ $t('Confirm') }}
                            </template>
                            <template v-if="form.processing">
                                <span class="sr-only">
                                    {{ $t('Removing') }}
                                </span>
                                <Loader2 class="animate-spin" />
                            </template>
                        </Button>
                    </div>
                </form>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
